/** Keys as MemberRecordLinkType::fields() lists them; the labels double as translation keys, server-side too. */
export const recordLinkFieldLabels: Record<string, string> = {
    firstName: 'Vorname',
    lastName: 'Nachname',
    dateOfBirth: 'Geburtsdatum',
    placeOfBirth: 'Geburtsort',
    email: 'E-Mail',
};

export const recordLinkFieldLabel = (field: string): string => recordLinkFieldLabels[field] ?? field;

export interface LinkedRecord {
    name: string
    institutionName: string
    isMain: boolean
}

export interface RecordLinkInfo {
    groupId: number
    isMain: boolean
    state: 'unresolved' | 'resolved' | 'conflict'
    mainInstitutionName: string | null
    linkedRecords: LinkedRecord[]
    divergentFields: string[]
    canClaim: boolean
}

/** Keys as MemberPairScorer emits them: the compared fields plus the login bonus; the labels double as translation keys. */
export const recordLinkSignalLabels: Record<string, string> = {
    ...recordLinkFieldLabels,
    login: 'Login',
};

export function recordLinkScoreLabel(score: number): string {
    if (score >= 100) {
        return 'Exakter Treffer';
    }

    return score >= 85 ? 'Sehr wahrscheinlich' : 'Möglich';
}

export function recordLinkScoreBadgeClass(score: number): string {
    if (score >= 100) {
        return 'kgr-badge--accent';
    }

    return score >= 85 ? 'kgr-badge--warning' : 'kgr-badge--info';
}

export type SignalStatus = 'exact' | 'fuzzy';

export interface SignalChip {
    field: string
    status: SignalStatus
}

export function recordLinkSignalChips(matchedSignals: Record<string, string>): SignalChip[] {
    return Object.entries(matchedSignals)
        .filter((entry): entry is [string, SignalStatus] => entry[1] === 'exact' || entry[1] === 'fuzzy')
        .map(([field, status]) => ({ field, status }));
}

export interface ComparableRecord {
    key: string | number
    displayName: string
    institutionName: string
    values: Record<string, string | null>
    divergentFields: string[]
}

export interface SuggestionMember {
    id: number
    displayName: string
    institutionId: number
    institutionName: string
    dateOfBirth: string | null
    email: string | null
    // not a compared field; shown next to a divergent last name because the scan matches it cross-wise
    birthName: string | null
    // federation payload only - the club API gets differs-flags instead
    values: Record<string, string | null>
}

export interface GroupRecord {
    entryId: number
    type: string
    recordId: number
    displayName: string
    institutionName: string
    isMain: boolean
    values: Record<string, string | null>
    // main-directional once a main record exists, otherwise "not all agree"
    divergentFields: string[]
}

export interface Group {
    id: number
    note: string | null
    state: 'unresolved' | 'resolved' | 'conflict'
    mainSource: string | null
    mainSetAt: string | null
    mainStale: boolean
    mainDecidedBy: string | null
    createdAt: string
    fields: string[]
    records: GroupRecord[]
}

export interface Suggestion {
    id: number
    score: number
    sameClub: boolean
    computedAt: string
    decidedAt: string | null
    // also null on dismissals written by an unlink
    decidedByName: string | null
    matchedSignals: Record<string, string>
    // the compared fields the exact-match rule rejects for this pair, in display order: the scan's verdict,
    // so the card never re-compares values with a rule of its own
    divergentFields: string[]
    // the records are listed once on the cluster
    memberIds: [number, number]
}

/** which list a card sits in: it decides the actions offered per cluster */
export type SuggestionListMode = 'suggestions' | 'same-club' | 'dismissed';

export type SuggestionBlocker = 'same_club_duplicate' | 'dismissed_pair' | 'multiple_groups';

/**
 * One connected component of pair findings as the server clusters it: one person, N records. The server
 * judges the whole cluster - pairs on other pages, dismissed pairs and existing groups included - so what
 * it marks exact is exactly what "link every exact match" will link.
 */
export interface SuggestionCluster {
    key: string
    /** every pair is an exact match and nothing blocks the link */
    exact: boolean
    /** why the cluster is not auto-linked although its pairs may be exact */
    blockers: SuggestionBlocker[]
    weakestScore: number
    /**
     * clubs holding more than one record of the cluster: a link is refused while such a same-club double row
     * exists, because it is a cleanup case for that club, never a link
     */
    duplicateInstitutionNames: string[]
    fields: string[]
    /** every field the scan rejected on any pair of the cluster, in display order */
    divergentFields: string[]
    members: SuggestionMember[]
    /** strongest pair first */
    suggestions: Suggestion[]
}
